import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Home, Edit2, Upload, FileText, MapPin, Eye, X } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { clearAppCache } from '@/utils/cacheUtils';

const emptyForm = {
  property_name: '', size: '', total_price: '', property_type: '', location: '',
  address: '', description: '', price_range: '', total_area: '', total_units: '',
  amenities: '', status: 'active', rera_number: '', google_maps_url: '',
  latitude: '', longitude: '', highlights: ''
};

const PropertyForm = ({ form, setForm, onSubmit, loading, submitLabel }) => (
  <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Label>Property Name *</Label><Input value={form.property_name} onChange={e => setForm({...form, property_name: e.target.value})} required placeholder="e.g., Ganesa Green City" /></div>
      <div><Label>Property Type *</Label>
        <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.property_type} onChange={e => setForm({...form, property_type: e.target.value})}>
          <option value="">Select Type</option>
          <option>Plot</option><option>Flat</option><option>Villa</option><option>Commercial</option><option>Farm House</option><option>Residential</option>
        </select>
      </div>
      <div><Label>Status</Label>
        <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
          <option value="active">Active</option><option value="upcoming">Upcoming</option><option value="sold_out">Sold Out</option>
        </select>
      </div>
      <div><Label>Location *</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g., Khatushyam" /></div>
      <div><Label>Size</Label><Input value={form.size} onChange={e => setForm({...form, size: e.target.value})} placeholder="e.g., 1200 sq ft" /></div>
      <div><Label>Total Price (₹) *</Label><Input type="number" value={form.total_price} onChange={e => setForm({...form, total_price: e.target.value})} required placeholder="0" /></div>
      <div><Label>Price Range (Display)</Label><Input value={form.price_range} onChange={e => setForm({...form, price_range: e.target.value})} placeholder="₹15L - ₹25L" /></div>
      <div><Label>Total Area</Label><Input value={form.total_area} onChange={e => setForm({...form, total_area: e.target.value})} placeholder="50 Acres" /></div>
      <div><Label>Total Units</Label><Input value={form.total_units} onChange={e => setForm({...form, total_units: e.target.value})} placeholder="200" /></div>
      <div><Label>RERA Number</Label><Input value={form.rera_number} onChange={e => setForm({...form, rera_number: e.target.value})} placeholder="RERA/UP/..." /></div>
      <div className="col-span-2"><Label>Full Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Full property address" /></div>
      <div className="col-span-2"><Label>Description</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Property description for website" /></div>
      <div className="col-span-2"><Label>Highlights (comma separated)</Label><Input value={form.highlights} onChange={e => setForm({...form, highlights: e.target.value})} placeholder="Gated Community, 24/7 Security, Park" /></div>
      <div className="col-span-2"><Label>Amenities (comma separated)</Label><Input value={form.amenities} onChange={e => setForm({...form, amenities: e.target.value})} placeholder="Water Supply, Electricity, Road, Temple" /></div>
    </div>
    <div className="border-t pt-4">
      <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><MapPin size={16} className="text-emerald-600" /> Google Maps Location</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Google Maps Embed URL</Label><Input value={form.google_maps_url} onChange={e => setForm({...form, google_maps_url: e.target.value})} placeholder="https://maps.google.com/..." /></div>
        <div><Label>Latitude</Label><Input value={form.latitude} onChange={e => setForm({...form, latitude: e.target.value})} placeholder="27.5530" /></div>
        <div><Label>Longitude</Label><Input value={form.longitude} onChange={e => setForm({...form, longitude: e.target.value})} placeholder="75.2697" /></div>
      </div>
    </div>
    <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
      {loading ? 'Saving...' : submitLabel}
    </Button>
  </form>
);

export default function Properties({ user, onLogout }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({...emptyForm});
  const [editFormData, setEditFormData] = useState({...emptyForm});

  useEffect(() => { fetchProperties(); }, []);

  const fetchProperties = async () => {
    setIsDataLoading(true);
    try {
      const response = await api.get('/properties');
      setProperties(response.data);
    } catch { toast.error('Failed to load properties'); }
    finally { setIsDataLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/properties', { ...formData, total_price: parseFloat(formData.total_price) });
      await clearAppCache();
      toast.success('Property added!');
      setDialogOpen(false);
      setFormData({...emptyForm});
      fetchProperties();
    } catch { toast.error('Failed to add property'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property?')) return;
    try {
      await api.delete(`/properties/${id}`);
      await clearAppCache();
      toast.success('Property deleted!');
      fetchProperties();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (prop) => {
    setEditingProperty(prop);
    setEditFormData({
      property_name: prop.property_name || '', size: prop.size || '', total_price: (prop.total_price || '').toString(),
      property_type: prop.property_type || '', location: prop.location || '', address: prop.address || '',
      description: prop.description || '', price_range: prop.price_range || '', total_area: prop.total_area || '',
      total_units: prop.total_units || '', amenities: prop.amenities || '', status: prop.status || 'active',
      rera_number: prop.rera_number || '', google_maps_url: prop.google_maps_url || '',
      latitude: prop.latitude || '', longitude: prop.longitude || '', highlights: prop.highlights || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/properties/${editingProperty.id}`, { ...editFormData, total_price: parseFloat(editFormData.total_price) });
      await clearAppCache();
      toast.success('Property updated!');
      setEditDialogOpen(false);
      fetchProperties();
    } catch { toast.error('Failed to update'); }
    finally { setLoading(false); }
  };

  const handlePdfUpload = async (propId, file) => {
    setUploadingPdf(propId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/properties/${propId}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('PDF uploaded!');
      fetchProperties();
    } catch { toast.error('Failed to upload PDF'); }
    finally { setUploadingPdf(null); }
  };

  const handleDeletePdf = async (propId, pdfId) => {
    try {
      await api.delete(`/properties/${propId}/pdf/${pdfId}`);
      toast.success('PDF deleted');
      fetchProperties();
    } catch { toast.error('Failed to delete'); }
  };

  const statusColor = (s) => {
    if (s === 'sold_out') return 'bg-red-100 text-red-700';
    if (s === 'upcoming') return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6" data-testid="properties-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Property Master</h1>
            <p className="text-sm text-slate-500">{properties.length} properties</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2"><Plus size={18} /> Add Property</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Add New Property</DialogTitle></DialogHeader>
              <PropertyForm form={formData} setForm={setFormData} onSubmit={handleSubmit} loading={loading} submitLabel="Add Property" />
            </DialogContent>
          </Dialog>
        </div>

        {/* Property Cards */}
        <div className="grid gap-4">
          {isDataLoading ? (
            <div className="text-center py-12 text-slate-500">Loading properties...</div>
          ) : properties.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No properties found. Add your first property!</div>
          ) : (
            properties.map(prop => (
              <Card key={prop.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Home size={18} className="text-emerald-600" />
                            <h3 className="font-bold text-lg text-slate-800">{prop.property_name}</h3>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor(prop.status)}`}>{prop.status || 'active'}</span>
                            {prop.property_type && <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{prop.property_type}</span>}
                          </div>
                          {prop.location && <div className="flex items-center gap-1 text-sm text-slate-500"><MapPin size={14} /> {prop.location}</div>}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg text-emerald-600">₹{(prop.total_price || 0).toLocaleString('en-IN')}</div>
                          {prop.price_range && <div className="text-xs text-slate-500">{prop.price_range}</div>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                        {prop.size && <span>Size: {prop.size}</span>}
                        {prop.total_area && <span>Area: {prop.total_area}</span>}
                        {prop.total_units && <span>Units: {prop.total_units}</span>}
                        {prop.rera_number && <span>RERA: {prop.rera_number}</span>}
                      </div>
                      {prop.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{prop.description}</p>}
                      {/* PDFs */}
                      {prop.pdf_maps && prop.pdf_maps.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {prop.pdf_maps.map(pdf => (
                            <div key={pdf.id} className="flex items-center gap-1 bg-slate-100 rounded-lg px-2 py-1 text-xs">
                              <FileText size={12} className="text-red-500" />
                              <a href={pdf.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{pdf.filename}</a>
                              <button onClick={() => handleDeletePdf(prop.id, pdf.id)} className="text-red-400 hover:text-red-600 ml-1"><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex lg:flex-col gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(prop)} className="gap-1"><Edit2 size={14} /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => { fileInputRef.current?.setAttribute('data-prop-id', prop.id); fileInputRef.current?.click(); }}
                        className="gap-1" disabled={uploadingPdf === prop.id}>
                        <Upload size={14} /> {uploadingPdf === prop.id ? 'Uploading...' : 'Upload PDF'}
                      </Button>
                      <a href={`/properties/${prop.id}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="gap-1 w-full"><Eye size={14} /> Website</Button>
                      </a>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(prop.id)}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Hidden file input */}
        <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
          onChange={(e) => {
            const propId = e.target.getAttribute('data-prop-id');
            if (e.target.files[0] && propId) handlePdfUpload(propId, e.target.files[0]);
            e.target.value = '';
          }} />

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Edit Property</DialogTitle></DialogHeader>
            <PropertyForm form={editFormData} setForm={setEditFormData} onSubmit={handleUpdate} loading={loading} submitLabel="Update Property" />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
